/**
 * The reward moment for a finished recipe.
 *
 * This is the beat the whole retention loop builds toward, and it used to be
 * spent on someone else's business: a "Save to your Cookbook?" modal took the
 * screen the instant a recipe completed, while the actual reward — XP, goals,
 * level, technique reps — rendered as a static receipt below the fold. Testers
 * cooked a recipe, completed two goals, and did not realise they had earned
 * anything.
 *
 * So this modal carries the reward and nothing else. No save prompt: the
 * Cookbook is a curated shelf, saving stays explicit and user-initiated, and it
 * is offered *after* this has been seen (see RecipeAudioPlayer's Cookbook
 * offer and the persistent control on the recipe itself).
 *
 * Four things land here, in the order they matter to someone who just cooked:
 *   1. XP, split by where it came from
 *   2. goals completed
 *   3. level progress, moving from where it was to where it is
 *   4. technique reps — the most differentiated thing the app does, and until
 *      now completely invisible at completion
 *
 * Colours come from brandColors only. White on GREEN is 3.3:1 and is never
 * used for text here; the green surfaces are GREEN_LIGHT with GREEN_TEXT on
 * top, which is the pairing the rest of the cooking view already ships.
 */
import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";

import { RED, RED_LIGHT, GREEN, GREEN_LIGHT, BORDER, MUTED } from "assets/theme/brandColors";
import { indefiniteArticle } from "utils/grammar";

// Body-weight green. Already the cooking view's text colour on GREEN_LIGHT
// (4.5:1) — GREEN itself is only 2.4:1 there and must not carry text.
const GREEN_TEXT = "#2e7d32";
const INK = "#222";

// Beyond this the technique list would push the level bar off a phone screen,
// which is the exact defect this component exists to fix.
const MAX_TECHNIQUE_ROWS = 3;

function TierChip({ tier, tierUp }) {
  if (!tier || tier === "unseen") return null;
  return (
    <Box
      component="span"
      sx={{
        fontSize: 10,
        fontWeight: 700,
        px: 0.75,
        py: "2px",
        borderRadius: "4px",
        whiteSpace: "nowrap",
        bgcolor: tierUp ? GREEN_LIGHT : BORDER,
        color: tierUp ? GREEN_TEXT : INK,
      }}
    >
      {tier}
    </Box>
  );
}

TierChip.propTypes = {
  tier: PropTypes.string,
  tierUp: PropTypes.bool,
};

function Row({ label, value, strong }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "baseline" }}>
      <Typography sx={{ fontSize: 13, color: INK, fontWeight: strong ? 700 : 400, minWidth: 0 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, color: GREEN_TEXT, fontWeight: 700, whiteSpace: "nowrap" }}>
        {value}
      </Typography>
    </Box>
  );
}

Row.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  strong: PropTypes.bool,
};

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 1,
        color: MUTED,
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

SectionLabel.propTypes = { children: PropTypes.node.isRequired };

function RewardMoment({ result, recipeTitle, hasWeekMoment, onClose }) {
  const open = !!result;
  // The bar animates from where the user stood to where they now stand, so the
  // level is something they watch move rather than a percentage they read.
  const [barFilled, setBarFilled] = useState(false);

  useEffect(() => {
    if (!open) {
      setBarFilled(false);
      return undefined;
    }
    const timer = setTimeout(() => setBarFilled(true), 250);
    return () => clearTimeout(timer);
  }, [open]);

  const goals = useMemo(() => (result?.goals_updated || []).filter((g) => g.completed), [result]);
  const techniques = result?.techniques_practiced || [];

  if (!result) return null;

  const totalXp = result.total_xp ?? 0;
  const recipeXp = result.xp_gained ?? 0;
  const level = result.level ?? 1;
  const floor = result.level_floor_xp ?? 0;
  const ceiling = result.next_level_total_xp ?? 0;
  const span = Math.max(1, ceiling - floor);
  const xpInLevel = Math.max(0, (result.points ?? 0) - floor);
  const fromPct = result.previous_progress_percent ?? 0;
  const toPct = result.progress_percent ?? 0;
  // A level-up resets the bar to near-empty, so animating backwards would read
  // as a loss. Start it at zero instead: the new level is the story.
  const startPct = result.leveled_up ? 0 : fromPct;

  const nextDistinction = Array.isArray(result.next_distinction) ? result.next_distinction : null;
  const shownTechniques = techniques.slice(0, MAX_TECHNIQUE_ROWS);
  const hiddenTechniques = techniques.length - shownTechniques.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      scroll="body"
      aria-labelledby="reward-moment-title"
    >
      {/* Headline band. Large bold white on RED is 7.9:1. */}
      <Box sx={{ bgcolor: RED, color: "#fff", px: 3, pt: 2.5, pb: 2.25, textAlign: "center" }}>
        <Typography
          id="reward-moment-title"
          sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" }}
        >
          Recipe complete
        </Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 500, mt: 0.25, opacity: 0.95 }}>
          {recipeTitle}
        </Typography>
        <Typography sx={{ fontSize: 42, fontWeight: 800, lineHeight: 1.1, mt: 1 }}>
          +{totalXp} XP
        </Typography>
      </Box>

      <Box sx={{ px: 3, py: 2.5 }}>
        {/* XP, split by where it came from */}
        <Box
          sx={{
            bgcolor: GREEN_LIGHT,
            border: `1px solid ${GREEN}`,
            borderRadius: 2,
            px: 1.75,
            py: 1.25,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <Row label={recipeTitle} value={`+${recipeXp} XP`} />
          {goals.map((g) => (
            <Row key={g.goal_id} label={`Goal · ${g.title}`} value={`+${g.xp_awarded || 0} XP`} />
          ))}
          {goals.length === 0 && (result.goal_xp ?? 0) > 0 && (
            <Row label="Goals" value={`+${result.goal_xp} XP`} />
          )}
        </Box>

        {/* Level progress */}
        <Box sx={{ mt: 2.25 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK }}>
              Level {level}
            </Typography>
            <Typography sx={{ fontSize: 11, color: MUTED }}>
              {xpInLevel} / {span} XP to Level {level + 1}
            </Typography>
          </Box>
          <Box
            sx={{ mt: 0.75, height: 8, borderRadius: 4, bgcolor: BORDER, overflow: "hidden" }}
            role="progressbar"
            aria-valuenow={Math.round(toPct)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <Box
              sx={{
                height: "100%",
                borderRadius: 4,
                bgcolor: RED,
                width: `${barFilled ? toPct : startPct}%`,
                transition: "width 0.9s ease-out",
              }}
            />
          </Box>

          {result.leveled_up && (
            <Box
              sx={{
                mt: 1.25,
                bgcolor: GREEN_LIGHT,
                border: `1px solid ${GREEN}`,
                borderRadius: 2,
                px: 1.75,
                py: 1,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: GREEN_TEXT }}>
                Level up — you reached Level {level}
              </Typography>
              {result.distinction_up && result.distinction && (
                <Typography sx={{ fontSize: 12, color: INK, mt: 0.25 }}>
                  You are now {indefiniteArticle(result.distinction)}{" "}
                  <strong>{result.distinction}</strong>
                  {nextDistinction
                    ? `. Level ${nextDistinction[0]} makes you ${indefiniteArticle(
                        nextDistinction[2]
                      )} ${nextDistinction[2]}.`
                    : "."}
                </Typography>
              )}
              {/* A rung that earns no new distinction still gets to point at
                  the one it is climbing toward. The level itself stays a bare
                  number — the destination is the distinction, never a title
                  invented for this level. */}
              {!result.distinction_up && nextDistinction && (
                <Typography sx={{ fontSize: 12, color: INK, mt: 0.25 }}>
                  Level {nextDistinction[0]} makes you {indefiniteArticle(nextDistinction[2])}{" "}
                  <strong>{nextDistinction[2]}</strong>.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Technique reps. The differentiated thing the app does, and the one
            part of a completion that says something about the cook rather than
            about their score. */}
        {shownTechniques.length > 0 && (
          <Box sx={{ mt: 2.25 }}>
            <SectionLabel>Skills practised</SectionLabel>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {shownTechniques.map((t) => (
                <Box
                  key={t.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.9,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK }}>
                        {t.display_name}
                      </Typography>
                      <TierChip tier={t.tier} tierUp={t.tier_up} />
                    </Box>
                    <Typography sx={{ fontSize: 11, color: MUTED, mt: 0.15 }}>
                      {t.tier_up
                        ? t.first_time
                          ? "First time — you've started this one"
                          : `Moved up from ${t.previous_tier}`
                        : t.reps_to_next != null
                        ? `${t.reps_to_next} more to ${nextRungLabel(t.tier)}`
                        : "Instinct — the top rung"}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 700, color: GREEN_TEXT, whiteSpace: "nowrap" }}
                  >
                    +{t.reps_gained} rep{t.reps_gained === 1 ? "" : "s"}
                  </Typography>
                </Box>
              ))}
            </Box>
            {hiddenTechniques > 0 && (
              <Typography sx={{ fontSize: 11, color: MUTED, mt: 0.75 }}>
                and {hiddenTechniques} more — see them all under Your Kitchen.
              </Typography>
            )}
          </Box>
        )}

        <Button
          fullWidth
          variant="contained"
          onClick={onClose}
          sx={{
            mt: 2.5,
            fontSize: 14,
            fontWeight: 700,
            textTransform: "none",
            bgcolor: RED,
            color: "#fff",
            "&:hover": { bgcolor: RED, color: "#fff", filter: "brightness(0.9)" },
          }}
        >
          {hasWeekMoment ? "See your week →" : "Nice — keep going"}
        </Button>
        {hasWeekMoment && (
          <Typography sx={{ fontSize: 11, color: MUTED, textAlign: "center", mt: 1 }}>
            That was the last recipe in this week.
          </Typography>
        )}
      </Box>
    </Dialog>
  );
}

// The rung above the one you are on. Kept as a lookup rather than derived from
// the tier list so a renamed rung fails visibly here instead of printing
// "undefined" in the middle of a sentence.
const NEXT_RUNG = {
  unseen: "Tried",
  Tried: "Practiced",
  Practiced: "Comfortable",
  Comfortable: "Instinct",
};

function nextRungLabel(tier) {
  return NEXT_RUNG[tier] || "the next rung";
}

RewardMoment.propTypes = {
  /** Completion response from POST /schedule/recipes/:id/complete, or null. */
  result: PropTypes.shape({
    total_xp: PropTypes.number,
    xp_gained: PropTypes.number,
    goal_xp: PropTypes.number,
    goals_updated: PropTypes.array,
    techniques_practiced: PropTypes.array,
    level: PropTypes.number,
    leveled_up: PropTypes.bool,
    points: PropTypes.number,
    level_floor_xp: PropTypes.number,
    next_level_total_xp: PropTypes.number,
    progress_percent: PropTypes.number,
    previous_progress_percent: PropTypes.number,
    distinction: PropTypes.string,
    distinction_up: PropTypes.bool,
    next_distinction: PropTypes.array,
  }),
  recipeTitle: PropTypes.string,
  /** True when the week finished with this recipe: the CTA hands over to it. */
  hasWeekMoment: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

RewardMoment.defaultProps = {
  result: null,
  recipeTitle: "this recipe",
  hasWeekMoment: false,
};

export default RewardMoment;
